/**
 * Component Showcase Page
 * Demonstrates all components from the custom components-lib
 * 
 * This page serves as a living documentation for our Chakra-compatible
 * component library, showing usage examples for each component.
 */

import React, { useState } from 'react';
import {
  // Theme
  ChakraProvider,
  extendTheme,
  useColorMode,
  useColorModeValue,
  
  // Primitives
  Box,
  Flex,
  Grid,
  GridItem,
  Stack,
  VStack,
  HStack,
  Text,
  Heading,
  Icon,
  Divider,
  Spacer,
  Badge,
  Link,
  
  // Layout
  SimpleGrid,
  Container,
  Center,
  
  // Forms
  Button,
  ButtonGroup,
  IconButton,
  Input,
  Select,
  Textarea,
  Switch,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  
  // Overlays
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  
  // Feedback
  Spinner,
  useToast,
  
  // Navigation
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  
  // Data Display
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  
  // Hooks
  useDisclosure,
} from '../components-lib';

// Additional components from chakra-compat
import {
  Avatar,
  AvatarGroup,
  Image,
  Checkbox,
  CheckboxGroup,
  CloseButton,
  Skeleton,
  SkeletonText,
  SkeletonCircle,
  Tooltip,
  Progress,
  ProgressLabel,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tag,
  TagLabel,
  TagCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Radio,
  RadioGroup,
} from '../components-lib/chakra-compat';

// Icons from react-icons
import { 
  MdSearch, 
  MdEmail, 
  MdPhone, 
  MdAdd, 
  MdEdit, 
  MdDelete,
  MdCheckCircle,
  MdWarning,
  MdInfo,
  MdError,
  MdChevronRight,
  MdMenu,
} from 'react-icons/md';

// Section component for organization
const Section = ({ title, children }) => (
  <Box mb={10} p={6} bg="white" borderRadius="lg" boxShadow="sm">
    <Heading size="lg" mb={4} color="brand.500">
      {title}
    </Heading>
    <Divider mb={4} />
    {children}
  </Box>
);

// Subsection for component groups
const SubSection = ({ title, children }) => (
  <Box mb={6}>
    <Text fontWeight="bold" fontSize="md" mb={3} color="gray.700">
      {title}
    </Text>
    {children}
  </Box>
);

const ComponentShowcase = () => {
  const { colorMode, toggleColorMode } = useColorMode();
  const bgColor = useColorModeValue('secondaryGray.300', 'navy.900');
  const toast = useToast();
  
  // Modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Form states
  const [inputValue, setInputValue] = useState('');
  const [selectValue, setSelectValue] = useState('');
  const [textareaValue, setTextareaValue] = useState('');
  const [switchValue, setSwitchValue] = useState(false);
  const [numberValue, setNumberValue] = useState(5);
  const [radioValue, setRadioValue] = useState('1');
  const [checkboxValues, setCheckboxValues] = useState(['1']);

  const showToast = (status) => {
    toast({
      title: `${status.charAt(0).toUpperCase() + status.slice(1)} Toast`,
      description: `This is a ${status} toast notification.`,
      status,
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Box bg={bgColor} minH="100vh" p={8}>
      <Container maxW="1200px">
        {/* Header */}
        <Box mb={8} textAlign="center">
          <Heading size="2xl" mb={4}>
            Component Library Showcase
          </Heading>
          <Text fontSize="lg" color="gray.600">
            Custom Chakra-compatible component library
          </Text>
          <Button 
            mt={4} 
            onClick={toggleColorMode}
            colorScheme="brand"
          >
            Toggle {colorMode === 'light' ? 'Dark' : 'Light'} Mode
          </Button>
        </Box>

        {/* ========== LAYOUT COMPONENTS ========== */}
        <Section title="Layout Components">
          <SubSection title="Box">
            <HStack spacing={4}>
              <Box p={4} bg="brand.100" borderRadius="md">
                Basic Box
              </Box>
              <Box p={4} bg="green.100" borderRadius="lg" boxShadow="md">
                Box with Shadow
              </Box>
              <Box p={4} borderWidth="2px" borderColor="brand.500" borderRadius="md">
                Box with Border
              </Box>
            </HStack>
          </SubSection>

          <SubSection title="Flex">
            <Flex bg="gray.100" p={4} borderRadius="md" justify="space-between" align="center">
              <Box bg="blue.100" p={2}>Start</Box>
              <Spacer />
              <Box bg="green.100" p={2}>Center</Box>
              <Spacer />
              <Box bg="red.100" p={2}>End</Box>
            </Flex>
          </SubSection>

          <SubSection title="Stack, VStack, HStack">
            <HStack spacing={8}>
              <VStack spacing={2} align="stretch">
                <Text fontWeight="bold">VStack</Text>
                <Box p={2} bg="brand.100">Item 1</Box>
                <Box p={2} bg="brand.200">Item 2</Box>
                <Box p={2} bg="brand.300">Item 3</Box>
              </VStack>
              <HStack spacing={2}>
                <Text fontWeight="bold">HStack:</Text>
                <Box p={2} bg="green.100">A</Box>
                <Box p={2} bg="green.200">B</Box>
                <Box p={2} bg="green.300">C</Box>
              </HStack>
            </HStack>
          </SubSection>

          <SubSection title="Grid">
            <Grid templateColumns="repeat(3, 1fr)" gap={4}>
              <GridItem bg="purple.100" p={4} borderRadius="md">
                Grid Item 1
              </GridItem>
              <GridItem bg="purple.200" p={4} borderRadius="md">
                Grid Item 2
              </GridItem>
              <GridItem bg="purple.300" p={4} borderRadius="md">
                Grid Item 3
              </GridItem>
            </Grid>
          </SubSection>

          <SubSection title="SimpleGrid">
            <SimpleGrid columns={4} spacing={4}>
              {[1, 2, 3, 4].map((i) => (
                <Box key={i} bg="orange.100" p={4} borderRadius="md" textAlign="center">
                  Item {i}
                </Box>
              ))}
            </SimpleGrid>
          </SubSection>

          <SubSection title="Center">
            <Center h="100px" bg="gray.100" borderRadius="md">
              <Text>Centered Content</Text>
            </Center>
          </SubSection>
        </Section>

        {/* ========== TYPOGRAPHY ========== */}
        <Section title="Typography">
          <SubSection title="Heading Sizes">
            <VStack align="stretch" spacing={2}>
              <Heading size="2xl">Heading 2XL</Heading>
              <Heading size="xl">Heading XL</Heading>
              <Heading size="lg">Heading LG</Heading>
              <Heading size="md">Heading MD</Heading>
              <Heading size="sm">Heading SM</Heading>
            </VStack>
          </SubSection>

          <SubSection title="Text">
            <VStack align="stretch" spacing={2}>
              <Text fontSize="xl" fontWeight="bold">Bold Large Text</Text>
              <Text fontSize="md">Regular Medium Text</Text>
              <Text fontSize="sm" color="gray.500">Small Gray Text</Text>
              <Text fontStyle="italic">Italic Text</Text>
              <Text textDecoration="underline">Underlined Text</Text>
            </VStack>
          </SubSection>
        </Section>

        {/* ========== FORM COMPONENTS ========== */}
        <Section title="Form Components">
          <SubSection title="Button Variants">
            <HStack spacing={4} flexWrap="wrap">
              <Button colorScheme="brand">Brand Solid</Button>
              <Button colorScheme="blue">Blue</Button>
              <Button colorScheme="green">Green</Button>
              <Button colorScheme="red">Red</Button>
              <Button variant="outline" colorScheme="brand">Outline</Button>
              <Button variant="ghost" colorScheme="brand">Ghost</Button>
              <Button variant="link" colorScheme="brand">Link</Button>
            </HStack>
          </SubSection>

          <SubSection title="Button Sizes">
            <HStack spacing={4} align="center">
              <Button size="xs">XS Button</Button>
              <Button size="sm">SM Button</Button>
              <Button size="md">MD Button</Button>
              <Button size="lg">LG Button</Button>
            </HStack>
          </SubSection>

          <SubSection title="Button States">
            <HStack spacing={4}>
              <Button isLoading colorScheme="brand">Loading</Button>
              <Button isDisabled colorScheme="brand">Disabled</Button>
              <Button leftIcon={<MdAdd />} colorScheme="green">With Icon</Button>
              <IconButton icon={<MdEdit />} aria-label="Edit" colorScheme="blue" />
            </HStack>
          </SubSection>

          <SubSection title="ButtonGroup">
            <ButtonGroup variant="outline" spacing={0}>
              <Button>Save</Button>
              <Button>Cancel</Button>
              <Button>Reset</Button>
            </ButtonGroup>
          </SubSection>

          <SubSection title="Input">
            <VStack spacing={4} align="stretch" maxW="400px">
              <Input placeholder="Basic Input" value={inputValue} onChange={(e) => setInputValue(e.target.value)} />
              <Input placeholder="Disabled Input" isDisabled />
              <Input placeholder="Invalid Input" isInvalid />
              <InputGroup>
                <InputLeftElement>
                  <Icon as={MdSearch} color="gray.400" />
                </InputLeftElement>
                <Input placeholder="Search..." />
              </InputGroup>
              <InputGroup>
                <Input placeholder="Email" />
                <InputRightElement>
                  <Icon as={MdEmail} color="gray.400" />
                </InputRightElement>
              </InputGroup>
            </VStack>
          </SubSection>

          <SubSection title="Select">
            <Select 
              placeholder="Select option" 
              maxW="300px"
              value={selectValue}
              onChange={(e) => setSelectValue(e.target.value)}
            >
              <option value="option1">Option 1</option>
              <option value="option2">Option 2</option>
              <option value="option3">Option 3</option>
            </Select>
          </SubSection>

          <SubSection title="Textarea">
            <Textarea 
              placeholder="Enter text..." 
              maxW="400px"
              value={textareaValue}
              onChange={(e) => setTextareaValue(e.target.value)}
            />
          </SubSection>

          <SubSection title="NumberInput">
            <NumberInput 
              value={numberValue} 
              onChange={(val) => setNumberValue(val)}
              min={0}
              max={100}
              maxW="200px"
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </SubSection>

          <SubSection title="Switch">
            <HStack>
              <Switch 
                isChecked={switchValue} 
                onChange={(e) => setSwitchValue(e.target.checked)}
                colorScheme="brand"
              />
              <Text>Toggle: {switchValue ? 'ON' : 'OFF'}</Text>
            </HStack>
          </SubSection>

          <SubSection title="Checkbox">
            <CheckboxGroup value={checkboxValues} onChange={setCheckboxValues}>
              <HStack spacing={4}>
                <Checkbox value="1">Option 1</Checkbox>
                <Checkbox value="2">Option 2</Checkbox>
                <Checkbox value="3">Option 3</Checkbox>
              </HStack>
            </CheckboxGroup>
          </SubSection>

          <SubSection title="Radio">
            <RadioGroup value={radioValue} onChange={setRadioValue}>
              <HStack spacing={4}>
                <Radio value="1">Radio 1</Radio>
                <Radio value="2">Radio 2</Radio>
                <Radio value="3">Radio 3</Radio>
              </HStack>
            </RadioGroup>
          </SubSection>

          <SubSection title="FormControl">
            <FormControl isRequired maxW="400px">
              <FormLabel>Email Address</FormLabel>
              <Input placeholder="Enter email" />
              <FormHelperText>We'll never share your email.</FormHelperText>
            </FormControl>
            <Box mt={4} />
            <FormControl isInvalid maxW="400px">
              <FormLabel>Password</FormLabel>
              <Input type="password" placeholder="Enter password" />
              <FormErrorMessage>Password is required.</FormErrorMessage>
            </FormControl>
          </SubSection>
        </Section>

        {/* ========== DATA DISPLAY ========== */}
        <Section title="Data Display">
          <SubSection title="Badge">
            <HStack spacing={4}>
              <Badge>Default</Badge>
              <Badge colorScheme="brand">Brand</Badge>
              <Badge colorScheme="green">Success</Badge>
              <Badge colorScheme="red">Error</Badge>
              <Badge colorScheme="yellow">Warning</Badge>
              <Badge colorScheme="purple">Purple</Badge>
            </HStack>
          </SubSection>

          <SubSection title="Avatar">
            <HStack spacing={4}>
              <Avatar name="John Doe" size="sm" />
              <Avatar name="Jane Smith" size="md" />
              <Avatar name="Bob Wilson" size="lg" />
              <Avatar src="https://bit.ly/dan-abramov" size="xl" />
            </HStack>
          </SubSection>

          <SubSection title="AvatarGroup">
            <AvatarGroup max={3}>
              <Avatar name="John Doe" />
              <Avatar name="Jane Smith" />
              <Avatar name="Bob Wilson" />
              <Avatar name="Alice Brown" />
              <Avatar name="Charlie Green" />
            </AvatarGroup>
          </SubSection>

          <SubSection title="Tag">
            <HStack spacing={4}>
              <Tag>Default</Tag>
              <Tag colorScheme="brand">
                <TagLabel>Brand Tag</TagLabel>
              </Tag>
              <Tag colorScheme="green">
                <TagLabel>Closable</TagLabel>
                <TagCloseButton />
              </Tag>
            </HStack>
          </SubSection>

          <SubSection title="Table">
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th isNumeric>Age</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>John Doe</Td>
                    <Td>john@example.com</Td>
                    <Td isNumeric>30</Td>
                  </Tr>
                  <Tr>
                    <Td>Jane Smith</Td>
                    <Td>jane@example.com</Td>
                    <Td isNumeric>25</Td>
                  </Tr>
                  <Tr>
                    <Td>Bob Wilson</Td>
                    <Td>bob@example.com</Td>
                    <Td isNumeric>35</Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
          </SubSection>

          <SubSection title="Card">
            <SimpleGrid columns={3} spacing={4}>
              <Card>
                <CardHeader>
                  <Heading size="md">Card Title</Heading>
                </CardHeader>
                <CardBody>
                  <Text>This is a basic card component.</Text>
                </CardBody>
                <CardFooter>
                  <Button colorScheme="brand" size="sm">Action</Button>
                </CardFooter>
              </Card>
              <Card variant="outline">
                <CardBody>
                  <Text>Outline variant card.</Text>
                </CardBody>
              </Card>
              <Card variant="elevated">
                <CardBody>
                  <Text>Elevated variant card.</Text>
                </CardBody>
              </Card>
            </SimpleGrid>
          </SubSection>
        </Section>

        {/* ========== FEEDBACK ========== */}
        <Section title="Feedback">
          <SubSection title="Alert">
            <VStack spacing={4} align="stretch">
              <Alert status="success">
                <AlertIcon />
                <AlertTitle>Success!</AlertTitle>
                <AlertDescription>Your action was successful.</AlertDescription>
              </Alert>
              <Alert status="error">
                <AlertIcon />
                <AlertTitle>Error!</AlertTitle>
                <AlertDescription>Something went wrong.</AlertDescription>
              </Alert>
              <Alert status="warning">
                <AlertIcon />
                <AlertTitle>Warning!</AlertTitle>
                <AlertDescription>Please be careful.</AlertDescription>
              </Alert>
              <Alert status="info">
                <AlertIcon />
                <AlertTitle>Info</AlertTitle>
                <AlertDescription>Here's some information.</AlertDescription>
              </Alert>
            </VStack>
          </SubSection>

          <SubSection title="Progress">
            <VStack spacing={4} align="stretch" maxW="400px">
              <Progress value={30} />
              <Progress value={60} colorScheme="green" />
              <Progress value={80} colorScheme="brand" />
              <Progress isIndeterminate colorScheme="blue" />
            </VStack>
          </SubSection>

          <SubSection title="Spinner">
            <HStack spacing={8}>
              <Spinner size="xs" />
              <Spinner size="sm" />
              <Spinner size="md" />
              <Spinner size="lg" />
              <Spinner size="xl" color="brand.500" />
            </HStack>
          </SubSection>

          <SubSection title="Skeleton">
            <VStack spacing={4} align="stretch" maxW="400px">
              <Skeleton isLoaded={false}>
                <Box h="40px" />
              </Skeleton>
              <SkeletonText noOfLines={3} />
              <HStack>
                <SkeletonCircle size="50px" />
                <SkeletonText noOfLines={2} flex="1" />
              </HStack>
            </VStack>
          </SubSection>

          <SubSection title="Toast">
            <HStack spacing={4}>
              <Button onClick={() => showToast('success')} colorScheme="green">
                Success Toast
              </Button>
              <Button onClick={() => showToast('error')} colorScheme="red">
                Error Toast
              </Button>
              <Button onClick={() => showToast('warning')} colorScheme="yellow">
                Warning Toast
              </Button>
              <Button onClick={() => showToast('info')} colorScheme="blue">
                Info Toast
              </Button>
            </HStack>
          </SubSection>
        </Section>

        {/* ========== NAVIGATION ========== */}
        <Section title="Navigation">
          <SubSection title="Breadcrumb">
            <Breadcrumb separator={<Icon as={MdChevronRight} color="gray.500" />}>
              <BreadcrumbItem>
                <BreadcrumbLink href="#">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink href="#">Products</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink href="#">Category</BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
          </SubSection>

          <SubSection title="Link">
            <HStack spacing={4}>
              <Link href="#">Basic Link</Link>
              <Link href="#" color="brand.500">Colored Link</Link>
              <Link href="https://example.com" isExternal>External Link ↗</Link>
            </HStack>
          </SubSection>

          <SubSection title="Tabs">
            <Tabs>
              <TabList>
                <Tab>Tab 1</Tab>
                <Tab>Tab 2</Tab>
                <Tab>Tab 3</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <Text>Content for Tab 1</Text>
                </TabPanel>
                <TabPanel>
                  <Text>Content for Tab 2</Text>
                </TabPanel>
                <TabPanel>
                  <Text>Content for Tab 3</Text>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </SubSection>

          <SubSection title="Menu">
            <Menu>
              <MenuButton as={Button} rightIcon={<Icon as={MdMenu} />}>
                Actions
              </MenuButton>
              <MenuList>
                <MenuItem>Download</MenuItem>
                <MenuItem>Create Copy</MenuItem>
                <MenuDivider />
                <MenuItem>Delete</MenuItem>
              </MenuList>
            </Menu>
          </SubSection>

          <SubSection title="Accordion">
            <Accordion allowToggle maxW="500px">
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    Section 1
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                </AccordionPanel>
              </AccordionItem>
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    Section 2
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  Ut enim ad minim veniam, quis nostrud exercitation.
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </SubSection>
        </Section>

        {/* ========== OVERLAY ========== */}
        <Section title="Overlay">
          <SubSection title="Modal">
            <Button onClick={onOpen} colorScheme="brand">
              Open Modal
            </Button>
            <Modal isOpen={isOpen} onClose={onClose}>
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Modal Title</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Text>
                    This is a modal dialog. You can put any content here.
                  </Text>
                </ModalBody>
                <ModalFooter>
                  <Button variant="ghost" mr={3} onClick={onClose}>
                    Cancel
                  </Button>
                  <Button colorScheme="brand" onClick={onClose}>
                    Confirm
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </SubSection>

          <SubSection title="Tooltip">
            <HStack spacing={4}>
              <Tooltip label="This is a tooltip">
                <Button>Hover me</Button>
              </Tooltip>
            </HStack>
          </SubSection>
        </Section>

        {/* ========== MISC ========== */}
        <Section title="Miscellaneous">
          <SubSection title="Divider">
            <VStack spacing={4} align="stretch">
              <Divider />
              <Divider borderColor="brand.500" />
            </VStack>
          </SubSection>

          <SubSection title="Close Button">
            <HStack spacing={4}>
              <CloseButton size="sm" />
              <CloseButton size="md" />
              <CloseButton size="lg" />
            </HStack>
          </SubSection>

          <SubSection title="Image">
            <Image 
              src="https://via.placeholder.com/200x150" 
              alt="Placeholder"
              borderRadius="md"
              fallbackSrc="https://via.placeholder.com/200x150?text=Fallback"
            />
          </SubSection>
        </Section>

        {/* Footer */}
        <Box textAlign="center" py={8}>
          <Text color="gray.500">
            Component Library v1.0.0 — Chakra UI Compatible
          </Text>
        </Box>
      </Container>
    </Box>
  );
};

export default ComponentShowcase;
